import axios from 'axios';
import zxcvbn from 'zxcvbn';
import sha1 from 'crypto-js/sha1';
import { setIn } from 'bonfire-kindling';

const PWNED_BY_RANGE_URL = 'https://api.pwnedpasswords.com/range';

export const isEmailValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Check password strength by estimating password complexity score, and then when
// the score is above 2 lookup the password in the Pwned dataset to check if it's compromised.
// Returns `true` when password is strong and `false` otherwise.
export const checkPasswordStrength = async (password, userData = []) => {
  const { score } = zxcvbn(password, userData);

  // -- Check the password strength score.
  // The score is an integer from 0-4 where:
  // 0 is too guessable, 1 is very guessable, 2 somewhat guessable,
  // 3 safely unguessable and 4 very unguessable.
  if (score > 2) {
    const passwordHash = sha1(password).toString();

    // -- Search the password hash in the Pwned dataset
    // https://haveibeenpwned.com/API/v3#SearchingPwnedPasswordsByRange
    const { status, data: pwnedHashes } = await axios.get(
      `${PWNED_BY_RANGE_URL}/${passwordHash.substring(0, 5)}`
    );

    if (status !== 200) {
      return false;
    }

    return !new RegExp(`${passwordHash.substring(5)}:[0-9]+`, 'i').test(pwnedHashes);
  }

  return false;
};

const setInError = (errors, innerError) => setIn(errors, innerError.path[0], innerError.message);

/**
 * Accepts a `Joi` object validation schema and returns a function that
 * expects a map of values to validate.
 *
 * @param {import('joi').ObjectSchema} schema The `Joi` object validation schema.
 * @param {import('joi').ValidationOptions} opts The `Joi` validation options.
 * @returns If validation is successful, returns `null`. Otherwise, returns
 * a map of invalid field names to errors.
 */
export const createFormValidator =
  (schema, opts = {}) =>
  values => {
    const { error } = schema.validate(values, { abortEarly: false, ...opts });
    return error ? error.details.reduce(setInError, {}) : null;
  };

/**
 * An asynchronous version of `createFormValidator()`.
 *
 * @param {import('joi').ObjectSchema} schema The `Joi` object validation schema.
 * @param {import('joi').ValidationOptions} opts The `Joi` validation options.
 * @returns If validation is successful, returns `null`. Otherwise, returns
 * a map of invalid field names to errors.
 */
export const createFormValidatorAsync =
  (schema, opts = {}) =>
  async values => {
    try {
      await schema.validateAsync(values, { abortEarly: false, ...opts });
      return null;
    } catch (err) {
      return err.details.reduce(setInError, {});
    }
  };
